import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, ListChecks, Mic, UtensilsCrossed, Users } from "lucide-react";
import { useApp } from "../lib/app";
import { Sheet } from "./bits";
import { useT } from "../lib/i18n";

const NAV = [
  ["/", "Home", Home], ["/tasks", "Tasks", ListChecks], [null, "Unload", Mic], ["/meals", "Meals", UtensilsCrossed], ["/family", "Family", Users],
];

export function UnloadSheet({ open, onClose }) {
  const nav = useNavigate();
  const t = useT();
  const go = (p) => { onClose(); nav(p); };
  const opts = [["speak", "🎤", "Speak", "Say it in any language"], ["scan", "📷", "Scan", "Milk card, dhobi slip, list"], ["type", "⌨️", "Type / paste", "A WhatsApp message or a list"]];
  return (
    <Sheet open={open} onClose={onClose} testid="unload-sheet">
      <h2 className="font-display text-2xl font-extrabold mb-1">{t("Empty your head 💭")}</h2>
      <p className="text-[#6B6680] mb-4">{t("Marathi, Hindi, English or a mix. Nothing is saved until you confirm.")}</p>
      <div className="grid gap-3">
        {opts.map(([k, e, tt, s]) => (
          <button key={k} data-testid={`unload-${k}-btn`} onClick={() => go(`/unload/${k}`)} className="ul-card flex items-center gap-4 p-4 text-left hover:-translate-y-0.5 transition-transform">
            <span className="text-3xl w-14 h-14 rounded-2xl bg-[#F3F0FF] flex items-center justify-center">{e}</span>
            <span><b className="font-display text-xl block">{t(tt)}</b><span className="text-sm text-[#6B6680]">{t(s)}</span></span>
          </button>
        ))}
      </div>
    </Sheet>
  );
}

function DemoBar() {
  const { demo, switchTo } = useApp();
  const nav = useNavigate();
  const t = useT();
  if (!demo) return null;
  return (
    <div data-testid="demo-banner" className="sticky top-0 z-40 bg-[#2A2540] text-white px-3 py-2 flex items-center justify-between gap-2 text-xs">
      <span className="font-bold whitespace-nowrap">{t("Demo family · dummy data")}</span>
      <div className="flex gap-1">
        {["meera", "arjun", "riya"].map((p) => (
          <button key={p} data-testid={`demo-switch-${p}`} onClick={() => { switchTo(p); nav("/"); }}
            className={`px-2.5 py-1.5 rounded-full font-bold capitalize ${demo.as === p ? "bg-[#FF6FA3] text-white" : "bg-white/10"}`}>{p}</button>
        ))}
      </div>
    </div>
  );
}

function InstallBanner() {
  const [evt, setEvt] = useState(null);
  const [show, setShow] = useState(false);
  const t = useT();
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone = window.matchMedia("(display-mode: standalone)").matches || navigator.standalone;
  useEffect(() => {
    if (localStorage.getItem("ul_install_seen") || standalone) return;
    const h = (e) => { e.preventDefault(); setEvt(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", h);
    if (ios) setShow(true);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, [ios, standalone]);
  if (!show) return null;
  const close = () => { localStorage.setItem("ul_install_seen", "1"); setShow(false); };
  return (
    <div data-testid="install-banner" className="mx-4 mt-3 ul-card p-3 flex items-center gap-3">
      <img src="/icons/icon-192.png" alt="" className="w-10 h-10 rounded-xl" />
      <p className="text-sm flex-1"><b>{t("Install UnloadIt")}</b><br /><span className="text-[#6B6680]">Don’t carry it alone. Just UnloadIt.</span>{ios && <><br />{t("Tap Share → Add to Home Screen")}</>}</p>
      {!ios && evt && <button data-testid="install-btn" className="ul-btn ul-primary !min-h-[40px] text-sm" onClick={() => { evt.prompt(); close(); }}>{t("Install")}</button>}
      <button data-testid="install-close" className="text-[#6B6680] px-2 text-xl" onClick={close} aria-label="close">×</button>
    </div>
  );
}

export default function Shell({ children, nav = true }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const t = useT();
  useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);
  return (
    <div className="min-h-screen bg-[#FBF9FF] flex justify-center">
      <div className="w-full max-w-[480px] min-h-screen relative bg-[#FBF9FF] sm:shadow-[0_0_60px_rgba(106,90,224,0.10)]">
        <DemoBar />
        <InstallBanner />
        <main key={loc.pathname} className="ul-rise px-4 pb-32">{children}</main>
        {nav && (
          <nav data-testid="bottom-nav" className="fixed bottom-0 z-40 w-full max-w-[480px] bg-white/90 backdrop-blur-xl border-t border-[#F0ECFB] px-2 pb-[env(safe-area-inset-bottom)]">
            <div className="grid grid-cols-5 items-end h-[68px]">
              {NAV.map(([to, label, Icon]) => to === null ? (
                <button key="unload" data-testid="nav-unload-btn" onClick={() => setOpen(true)} className="flex flex-col items-center -mt-7">
                  <span className="ul-pulse w-[62px] h-[62px] rounded-full flex items-center justify-center text-white shadow-lg" style={{ background: "linear-gradient(135deg,#FF6FA3,#6A5AE0)" }}>
                    <Icon size={28} />
                  </span>
                  <span className="text-[11px] font-extrabold text-[#FF6FA3] mt-1">{t("Unload")}</span>
                </button>
              ) : (
                <NavLink key={to} to={to} end data-testid={`nav-${label.toLowerCase()}`}
                  className={({ isActive }) => `flex flex-col items-center gap-1 pb-2 text-[11px] font-extrabold ${isActive ? "text-[#6A5AE0]" : "text-[#9C97AE]"}`}>
                  <Icon size={22} />{t(label)}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
        <UnloadSheet open={open} onClose={() => setOpen(false)} />
      </div>
    </div>
  );
}

export const Plain = ({ children }) => (
  <div className="min-h-screen bg-[#FBF9FF] flex justify-center">
    <div className="w-full max-w-[480px] min-h-screen px-5 pb-16 relative overflow-hidden ul-rise">{children}</div>
  </div>
);
